import { MarkerArea } from '../markerArea/markerArea'
import { PositionHandler } from './PositionHandler'
import { ScaleHandler } from './ScaleHandler'
import { MarkerModes, MarkerState, MarkerTypeName } from '../markerArea/types'
import { Point2D } from 'Common/types/generic/Points'
import { BoundingAxis } from './types'
import type { BaseMarker, TransformMatrixType } from '../markerArea/types'
import { useImagesStore } from 'Store/pages/imageReview/imagesStore'

export interface IImageAnnotator {
  imageElement: HTMLImageElement
  originalImageElement: HTMLImageElement
  imageAspectRatio: number
  parentNode: HTMLElement
  canvas: HTMLCanvasElement
  container: HTMLElement
  contentContainer: HTMLElement
  markerArea: MarkerArea
  positionHandler: PositionHandler
  scaleHandler: ScaleHandler
  isPanning: boolean
  isPressed: boolean
  isMousedOver: boolean
  isFocused: boolean
  _height: number
  _width: number
  originalParentHeight: number
  originalParentWidth: number
  boundingAxis: BoundingAxis
  zIndex: number
  resizeObserver: ResizeObserver
  isMarkersInteractive: boolean
  onMarkerSelect: (marker) => void
  onMarkerDeselect: (marker) => void
  onMarkerMouseEnter: (marker) => void
  onMarkerMouseLeave: (marker) => void
  onMarkerCreated: (marker) => void
  onMarkerEdit: (marker) => void
  onZoom: (zoom: number) => void
  onTransform: (position: Point2D, transform: TransformMatrixType) => void
  onReady: () => void
}

export class ImageAnnotator implements IImageAnnotator {
  imageElement: HTMLImageElement
  originalImageElement: HTMLImageElement
  imageAspectRatio: number
  parentNode: HTMLElement
  canvas: HTMLCanvasElement
  container: HTMLElement
  contentContainer: HTMLElement
  markerArea: MarkerArea
  positionHandler: PositionHandler
  scaleHandler: ScaleHandler
  isPanning = false
  isPressed = false
  isMousedOver = false
  isFocused = false
  _height = 0
  _width = 0
  parentNodeHeight: number
  parentNodeWidth: number
  _zIndex = 5
  boundingAxis: BoundingAxis
  resizeObserver: ResizeObserver
  isMarkersInteractive: boolean
  onMarkerSelect: (marker: any) => void
  onMarkerDeselect: (marker: any) => void
  onMarkerMouseEnter: (marker: any) => void
  onMarkerMouseLeave: (marker: any) => void
  onMarkerCreated: (marker: any) => void
  onMarkerEdit: (marker: any) => void
  onZoom: (zoom: number) => void
  onTransform: (position: Point2D, transform: TransformMatrixType) => void
  onReady: () => void

  constructor(
    imageElementNode?: HTMLImageElement | undefined,
    options: { isMarkersInteractive?: boolean } = { isMarkersInteractive: true }
  ) {
    this.onParentElementResized = this.onParentElementResized.bind(this)
    this.checkForFocus = this.checkForFocus.bind(this)
    this.onImageLoad = this.onImageLoad.bind(this)
    this.onWheel = this.onWheel.bind(this)
    this.onMouseOver = this.onMouseOver.bind(this)
    this.onMouseLeave = this.onMouseLeave.bind(this)
    this.onPointerDown = this.onPointerDown.bind(this)
    this.onPointerUp = this.onPointerUp.bind(this)
    this.onPointerMove = this.onPointerMove.bind(this)
    this.onKeyDown = this.onKeyDown.bind(this)
    this.onKeyUp = this.onKeyUp.bind(this)
    this.onPositionHandlerTransform = this.onPositionHandlerTransform.bind(this)
    this.isMarkersInteractive = options.isMarkersInteractive

    if (imageElementNode) {
      this.setImage(imageElementNode)
    }
  }

  createElements() {
    this.canvas = document.createElement('canvas')
    this.container = document.createElement('div')
    this.contentContainer = document.createElement('div')
  }

  /**
   *
   * @returns boolean - Returns true if the image has been loaded into the DOM
   */
  getIsImageLoaded() {
    const imagesStore = useImagesStore()
    return imagesStore.currentImageLoaded
  }

  /**
   * Stores the parent node dimensions to be used in resize observer callback comparision
   */
  setParentNodeDimensions() {
    const parentNodeBoundingRect = this.parentNode.getBoundingClientRect()
    this.parentNodeHeight = parentNodeBoundingRect.height
    this.parentNodeWidth = parentNodeBoundingRect.width
  }

  /**
   * Sets the HTMLImage element to be annotated -
   * If the image is loaded in the rest of the ImageAnnotation elements will be setup
   * Otherwise an on load event listener will be binded to the image will subsequently
   * makes calls to setup the rest of the ImageAnnotation elements.
   * @param imageElement HTMLImageElement
   */
  setImage(imageElementNode: HTMLImageElement): void {
    if (this.imageElement) this.close()
    this.imageElement = imageElementNode.cloneNode() as HTMLImageElement
    //TODO Remove once getImageToCanvasAlignmentMatrix issue has been resolved
    //see getImageToCanvasAlignmentMatrix method in this class for detail
    this.originalImageElement = imageElementNode
    this.originalImageElement.style.opacity = '0'
    this.parentNode = imageElementNode.parentNode as HTMLElement
    this.setParentNodeDimensions()

    const isImageLoaded = this.getIsImageLoaded()
    if (isImageLoaded) {
      this.setup()
    } else {
      this.imageElement.addEventListener('load', this.onImageLoad)
    }
  }

  /**
   * Sets up all the neccesary DOM nodes, event listerners, and the MarkerArea
   */
  setup() {
    //Note: The order in which these functions are called matters.
    //As elements and height values must be available prior to
    //creating markers area, setting up the elements, and
    //attaching events.

    this.createElements()
    this.calculateAspectRatio()
    this.setupAxis()
    this.setupDimensions()
    this.markerArea = new MarkerArea(this.canvas, this._height, this.parentNode.clientWidth)
    this.markerArea.pixelRatio = this.getCanvasPixelRatio()
    this.markerArea.boundingBox = { width: this.imageElement.naturalWidth, height: this.imageElement.naturalHeight }
    this.markerArea.open()
    this.markerArea.render()
    this.setupContainers()
    this.setupImage()
    this.setupCanvas()
    this.attachEvents()
    this.attachMarkerAreaEvents()
    this.markerArea.baseTransformMatrix = this.getImageToCanvasAlignmentMatrix()
    this.positionHandler = new PositionHandler(this.imageElement, this.markerArea)
    this.attachPositionHandlerEvents()
    this.scaleHandler = new ScaleHandler(
      this.originalImageElement.naturalHeight,
      this.originalImageElement.getBoundingClientRect().height
    )
    if (typeof this.onReady === 'function') this.onReady()
  }

  calculateAspectRatio() {
    const naturalHeight = this.imageElement.naturalHeight
    const naturalWidth = this.imageElement.naturalWidth
    this.imageAspectRatio = naturalWidth / naturalHeight
  }

  setupAxis() {
    const imageWidthGivenFullHeight = this.parentNodeHeight * this.imageAspectRatio
    const isFullHeightImageWiderThanParent = imageWidthGivenFullHeight > this.parentNodeWidth
    this.boundingAxis = isFullHeightImageWiderThanParent ? BoundingAxis.Horizontal : BoundingAxis.Vertical
  }

  setupDimensions() {
    const imageWidthGivenFullHeight = this.parentNodeHeight * this.imageAspectRatio
    const imageHeightGivenFullWidth = this.parentNodeWidth / this.imageAspectRatio

    switch (true) {
      case this.boundingAxis === BoundingAxis.Horizontal:
        {
          this._height = imageHeightGivenFullWidth
          this._width = this.parentNodeWidth
        }
        break
      case this.boundingAxis === BoundingAxis.Vertical:
        {
          this._height = this.parentNodeHeight
          this._width = imageWidthGivenFullHeight
        }
        break
    }
  }

  /**
   * Sets up the content container styling to match the positioning, height, and width of the original image.
   */
  setupContentContainerStyles() {
    this.contentContainer.style.position = 'absolute'
    this.contentContainer.style.height = `${this._height}px`
    this.contentContainer.style.width = `${this._width}px`

    switch (true) {
      case this.boundingAxis === BoundingAxis.Horizontal:
        {
          this.contentContainer.style.top = `${(this.parentNodeHeight - this._height) / 2}px`
          this.contentContainer.style.left = `0px`
        }
        break
      case this.boundingAxis === BoundingAxis.Vertical:
        {
          this.contentContainer.style.top = `0px`
          this.contentContainer.style.left = `${(this.parentNodeWidth - this._width) / 2}px`
        }
        break
    }
  }

  /**
   * Sets up all the wrapper dom nodes needed for proper image and canvas positioning
   */
  setupContainers() {
    this.setupContentContainerStyles()
    this.container.style.position = 'absolute'
    this.container.style.top = '0'
    this.container.style.left = '0'
    this.container.style.height = '100%'
    this.container.style.width = '100%'
    this.container.style.overflow = 'hidden'
    this.container.style.zIndex = `${this.zIndex}`

    this.container.appendChild(this.contentContainer)
    this.parentNode.insertBefore(this.container, this.parentNode.firstChild)
  }

  /**
   * Sets up the canvas to properly overlay over the image within the contentContainter
   */
  setupCanvas() {
    if (this.contentContainer) {
      this.canvas.style.width = '100%'
      this.canvas.style.height = '100%'
      this.canvas.style.position = 'absolute'
      this.canvas.style.top = '0'
      this.canvas.style.left = '0'
      this.canvas.style.zIndex = '1'
      this.container.appendChild(this.canvas)
    } else throw new Error('Content container must be setup prior to canvas')
  }

  /**
   * Sets up the provided image dom node to properly display within the contentContainer
   */
  setupImage() {
    if (this.contentContainer) {
      this.imageElement.removeAttribute('style')
      this.imageElement.classList.remove(...this.imageElement.classList)
      this.imageElement.style.height = '100%'
      this.imageElement.style.width = '100%'
      this.imageElement.style.position = 'absolute'
      this.imageElement.style.top = '0'
      this.imageElement.style.left = '0'
      this.imageElement.style.zIndex = '0'
      this.imageElement.style.userSelect = 'none'
      this.contentContainer.appendChild(this.imageElement)
    } else throw new Error('Content container must be setup prior to canvas')
  }

  /**
   * Attaches all neccesary events for the main container, content container, and window
   */
  attachEvents() {
    this.container.addEventListener('wheel', this.onWheel, { passive: false })
    this.container.addEventListener('pointerdown', this.onPointerDown)
    this.container.addEventListener('pointerup', this.onPointerUp)
    this.container.addEventListener('pointermove', this.onPointerMove)
    this.container.addEventListener('mouseover', this.onMouseOver)
    this.container.addEventListener('mouseleave', this.onMouseLeave)
    document.addEventListener('keydown', this.onKeyDown)
    document.addEventListener('keyup', this.onKeyUp)
    this.resizeObserver = new ResizeObserver((e) => this.onParentElementResized(e))
    this.resizeObserver.observe(this.parentNode)
  }

  /**
   * Attaches callbacks passed to this ImageAnnotator class to the corresponding events in marker area.
   */
  attachMarkerAreaEvents() {
    if (this.markerArea) {
      this.markerArea.addEventListener('markermouseenter', this.onMarkerMouseEnter)
      this.markerArea.addEventListener('markermouseleave', this.onMarkerMouseLeave)
      this.markerArea.addEventListener('markerselect', this.onMarkerSelect)
      this.markerArea.addEventListener('markerdeselect', this.onMarkerDeselect)
      this.markerArea.addEventListener('markercreate', this.onMarkerCreated)
      this.markerArea.addEventListener('markerchange', this.onMarkerEdit)
    }
  }

  detachMarkerAreaEvents() {
    if (this.markerArea) {
      this.markerArea.removeEventListener('markermouseenter', this.onMarkerMouseEnter)
      this.markerArea.removeEventListener('markermouseleave', this.onMarkerMouseLeave)
      this.markerArea.removeEventListener('markerselect', this.onMarkerSelect)
      this.markerArea.removeEventListener('markerdeselect', this.onMarkerDeselect)
      this.markerArea.removeEventListener('markercreate', this.onMarkerCreated)
      this.markerArea.removeEventListener('markerchange', this.onMarkerEdit)
    }
  }

  onPositionHandlerTransform(position: Point2D, TransformMatrixType: TransformMatrixType) {
    if (typeof this.onTransform === 'function') {
      this.onTransform(position, TransformMatrixType)
    }
  }

  /**
   * Attaches callbacks passed to this ImageAnnotator class to the corresponding events in PositionHandler
   */
  attachPositionHandlerEvents() {
    if (this.positionHandler) {
      this.positionHandler.onTransform = this.onPositionHandlerTransform
    }
  }

  detachPositionHandlerEvents() {
    if (this.positionHandler) {
      this.positionHandler.onTransform = null
    }
  }

  /**
   * Detaches all events attached within the attachEvents method
   */
  detachEvents() {
    if (this.container) {
      this.container.removeEventListener('wheel', this.onWheel)
      this.container.removeEventListener('pointerdown', this.onPointerDown)
      this.container.removeEventListener('pointerup', this.onPointerUp)
      this.container.removeEventListener('pointermove', this.onPointerMove)
      this.container.removeEventListener('mouseover', this.onMouseOver)
      this.container.removeEventListener('mouseleave', this.onMouseLeave)
    }
    document.removeEventListener('keydown', this.onKeyDown)
    document.removeEventListener('keyup', this.onKeyUp)
    if (this.resizeObserver) this.resizeObserver.disconnect()
    this.resizeObserver = null
  }

  /**
   * Calculates a baseMatrix to apply to marker area, such that the canvas pixel ratio and pixel coordinate
   * lines up with the image being annotated.
   */
  getImageToCanvasAlignmentMatrix(): TransformMatrixType {
    //TODO Figure out how to calculate alignment matrix based off the imageElement node instead
    //of having to store the original image node.
    //The original image node is stored as there is a need when resizing the page.
    //The imageElement node may be scaled which impacts the imageBoundingRect.left value
    //This in turn miscalculates the imageToCanvasHorizontalAlignment.

    //Note: imageToCanvasHorizontalAlignment & imageToCanvasVerticalAlignment
    //has an error margin of ~0.02% due to differences in how
    //between how they and contentContainer top/left offsets are calculated.
    //For reference - in a 4000 px wide image - this is a margin of error of about a pixel

    const imgBoundingRect = this.imageElement.getBoundingClientRect()
    const canvasBoundingRect = this.canvas.getBoundingClientRect()
    const imageLeft = imgBoundingRect.left
    const canvasLeft = canvasBoundingRect.left
    const imageTop = imgBoundingRect.top
    const canvasTop = canvasBoundingRect.top
    const screenToImageScale = this.getScreenToImageScalingFactor()
    const imageToCanvasHorizontalAlignment: number = imageLeft - canvasLeft
    const imageToCanvasVerticalAlignment: number = imageTop - canvasTop

    const imageToCanvasAlignmentMatrix = [
      screenToImageScale,
      0,
      0,
      screenToImageScale,
      imageToCanvasHorizontalAlignment,
      imageToCanvasVerticalAlignment,
    ] as TransformMatrixType
    return imageToCanvasAlignmentMatrix
  }

  /**
   * Calculates appropriate pixel ratio to apply to the canvas
   */
  getCanvasPixelRatio(): number {
    const imagePixelDensity = this.getImagePixelRatio()
    return Math.max(imagePixelDensity, window.devicePixelRatio)
  }

  /**
   * Calculates the pixel ratio proportion of the images natural to displayed height.
   */
  getImagePixelRatio(): number {
    return this.imageElement.naturalHeight / this._height
  }

  getScreenToImageScalingFactor(): number {
    const imageBoundingRect = this.originalImageElement.getBoundingClientRect()
    if (this.boundingAxis === BoundingAxis.Horizontal) {
      return imageBoundingRect.width / this.originalImageElement.naturalWidth
    }

    return imageBoundingRect.height / this.originalImageElement.naturalHeight
  }

  /**
   * Recalculates the position coordinates to center the image within the container.
   * Useful when the container dimensions change (e.g. sidebar toggle).
   */
  reCenter(): void {
    if (!this.positionHandler || !this.imageElement) return

    // 1. Get the new dimensions of the container
    const containerWidth = this.parentNode.clientWidth
    const containerHeight = this.parentNode.clientHeight

    // 2. Get the current dimensions of the image (including the current scale)
    const imgRect = this.imageElement.getBoundingClientRect()

    // Note: We use the rect width/height because it accounts for the CSS scale transform
    const imgWidth = imgRect.width
    const imgHeight = imgRect.height

    // 3. Calculate the top-left coordinate (tx, ty) required to center the image
    // Formula: (Container_Dimension - Image_Dimension) / 2
    const centerX = (containerWidth - imgWidth) / 2
    const centerY = (containerHeight - imgHeight) / 2

    // 4. Update the PositionHandler directly
    // This updates the internal state and forces a CSS transform update
    this.positionHandler.position = { x: centerX, y: centerY }
    this.positionHandler.isDirty = true
    this.positionHandler.applyTransform()
  }

  /**
   * Callback function for parent element observer resize event.
   */
  onParentElementResized(e) {
    const newParentNodeHeight = e[0].contentRect.height
    const newParentNodeWidth = e[0].contentRect.width
    const heightsEqual = this.parentNodeHeight === newParentNodeHeight
    const widthsEqual = this.parentNodeWidth === newParentNodeWidth

    if (!heightsEqual || !widthsEqual) {
      // 1. Capture BOTH the zoom level and the exact pan position
      const previousZoom = this.zoom
      const previousPosition = {
        x: this.positionHandler.position.x,
        y: this.positionHandler.position.y,
      }

      // 2. Temporarily reset to 1 (fixes the annotation alignment drift)
      this.positionHandler.zoom(1)

      this.setParentNodeDimensions()
      this.setupAxis()
      this.setupDimensions()
      this.setupContentContainerStyles()
      this.markerArea.resize(
        this.parentNode.getBoundingClientRect().height,
        this.parentNode.getBoundingClientRect().width
      )
      this.markerArea.pixelRatio = this.getCanvasPixelRatio()
      this.markerArea.baseTransformMatrix = this.getImageToCanvasAlignmentMatrix()

      // 3. Restore the zoom level
      // (This calculates a default "centered" position, which we will immediately overwrite)
      this.zoom = previousZoom

      // 4. Force restore the previous pan position
      this.positionHandler.position = previousPosition
      this.positionHandler.updateTransformMatrix()
      this.positionHandler.applyTransform()
    }
  }

  /**
   * Callback function for when the image has succesfully loaded into the DOM
   */
  onImageLoad() {
    this.setup()
  }

  checkForFocus(e: PointerEvent | MouseEvent) {
    if (e.target instanceof Node) {
      const containsTarget = this.container.contains(e.target)
      if (containsTarget) {
        this.isFocused = true
        this.markerArea.isInteractive = this.isMarkersInteractive ? !this.isPanning : this.isMarkersInteractive
      } else {
        this.isFocused = false
        this.markerArea.isInteractive = false
        e.stopPropagation()
      }
    }
  }

  /**
   * Callback function for when the onWheel event
   */
  onWheel(e: WheelEvent) {
    if (!this.isFocused) return
    if (!this.scaleHandler) return
    const zoomPoint = { x: e.clientX, y: e.clientY }
    const scaleStepsLength = this.scaleHandler.scaleSteps.length
    const scaleStepsGridThreshold = scaleStepsLength - Math.round(scaleStepsLength * 0.35)
    if (e.deltaY < 0) {
      const nextZoomScale = this.scaleHandler.getNextScale()
      const scaleIndex = this.scaleHandler.getScaleIndex()
      if (nextZoomScale) {
        if (scaleIndex >= scaleStepsGridThreshold) this.markerArea.showGrid = true
        this.positionHandler.zoom(nextZoomScale, zoomPoint)
        if (this.onZoom && typeof this.onZoom === 'function') {
          this.onZoom(this.zoom)
        }
      }
    } else {
      const prevZoomScale = this.scaleHandler.getPreviousScale()
      const scaleIndex = this.scaleHandler.getScaleIndex()
      if (prevZoomScale) {
        if (scaleIndex < scaleStepsGridThreshold) this.markerArea.showGrid = false
        this.positionHandler.zoom(prevZoomScale, zoomPoint)
        if (this.onZoom && typeof this.onZoom === 'function') {
          this.onZoom(this.zoom)
        }
      }
    }
  }

  /**
   * Callback function for when the mouseover event occurs on the main container
   */
  onMouseOver(e: MouseEvent) {
    this.checkForFocus(e)
    this.isMousedOver = true
  }

  /**
   * Callback function for when the mouseleave event occurs on the main container
   */
  onMouseLeave(e: MouseEvent) {
    this.isMousedOver = false
    if (this.markerArea.currentMarker?.state.mode !== MarkerModes.Create) {
      this.isFocused = false
      this.markerArea.isInteractive = false
    }
  }

  /**
   * Callback function for when the pointerup event occurs on the main container
   */
  onPointerUp(e: PointerEvent) {
    if (!this.isFocused) return
    this.isPressed = false
    if (this.isPanning) {
      this.canvas.style.cursor = 'grab'
    }
  }

  /**
   * Callback function for when the pointerdown event occurs on the main container
   */
  onPointerDown(e: PointerEvent) {
    this.checkForFocus(e)
    if (!this.isFocused) return
    if (this.isMousedOver) {
      this.isPressed = true
      if (this.isPanning) {
        this.canvas.style.cursor = 'grabbing'
        this.positionHandler.previousPanPoint = { x: e.clientX, y: e.clientY }
      }
    }
  }

  /**
   * Callback function for when the pointermove event occurs on the main container
   */
  onPointerMove(e: PointerEvent) {
    if (!this.isFocused) return
    if (this.isPressed && this.isPanning) {
      this.positionHandler.pan({ x: e.clientX, y: e.clientY })
    }
    e.preventDefault()
  }

  /**
   * Callback function for when the keydown event occurs on the window
   */
  onKeyDown(e: KeyboardEvent) {
    if (e.repeat) return
    if (this.isMousedOver && e.code === 'Space') {
      this.markerArea.isInteractive = false
      //Only allow panning if zoomed in
      if (this.zoom <= 1) {
        this.canvas.style.cursor = 'not-allowed'
        return
      }
      this.isPanning = true
      this.canvas.style.cursor = 'grab'
    }
  }

  /**
   * Callback function for when the keyup event occurs on the window
   */
  onKeyUp(e: KeyboardEvent) {
    if (e.code === 'Space') {
      this.isPanning = false
      this.markerArea.isInteractive = this.isMarkersInteractive
      this.markerArea.updateCursor()
    }
  }

  /**
   * Resumes the marker area render loop
   */
  resume(): void {
    this.markerArea.render()
  }

  /**
   * Pause the marker area render loop
   */
  pause(): void {
    this.markerArea.pause()
  }

  /**Closes markerarea and cleans up all resources */
  close(): void {
    this.detachEvents()
    this.detachMarkerAreaEvents()
    this.detachPositionHandlerEvents()
    this.markerArea?.close()
    this.imageElement?.removeEventListener('load', this.onImageLoad)
    this.container?.remove()
    this.container = null
    this.contentContainer = null
    this.canvas = null
  }

  /**
   * Loads markers from existing state object
   * @param markerState
   */
  loadMarkers(markerState: Array<MarkerState>): void {
    this.markerArea.loadMarkers(markerState)
  }

  /**
   * Clears all the markers loaded into the marker area
   */
  clearMarkers(): void {
    this.markerArea.clearMarkers()
  }

  /**
   * Creates a new marker of the specified type
   * @param markerType Type of marker
   */
  createNewMarker(markerType: MarkerTypeName) {
    if (this.isFocused === false) {
      this.isFocused = true
      this.markerArea.isInteractive = this.isMarkersInteractive
    }
    this.markerArea.abortMarkerCreation()
    this.markerArea.createNewMarker(markerType)
  }

  /**
   * Removes the provided marker
   * @param marker MarkerArea BaseMarker or any decendents
   */
  removeMarker(marker: BaseMarker<any, any, any, any>) {
    this.markerArea.removeMarker(marker)
  }

  /**
   * Select marker by it's index
   * @param index Index of marker
   */
  selectMarker(index: number): void {
    const marker = this.markerArea.markers[index]
    this.markerArea.currentMarker = marker
    marker.state.isSelected = true
  }

  /**
   * Deselects the currently selected marker
   */
  deselectMarker(): void {
    if (this.markerArea.currentMarker) this.markerArea.currentMarker.state.isSelected = false
    this.markerArea.currentMarker = null
  }

  /**
   * Deletes marker at the provided index
   * @param index Index of the marker
   */
  deleteMarker(marker: BaseMarker<any, any, any, any>): void {
    this.markerArea.removeMarker(marker)
  }

  /**
   * Sets the zoom level for the ImageAnnotator and zooms in by scaling the container to the center point
   */
  set zoom(zoomPercentage: number) {
    if (this.scaleHandler) {
      const scale = this.scaleHandler.getScaleFromPercentage(zoomPercentage)
      this.scaleHandler.scale = scale

      const scaleStepsLength = this.scaleHandler.scaleSteps.length
      const scaleStepsGridThreshold = scaleStepsLength - Math.round(scaleStepsLength * 0.35)
      const scaleIndex = this.scaleHandler.getScaleIndex()

      if (scaleIndex >= scaleStepsGridThreshold) this.markerArea.showGrid = true
      else this.markerArea.showGrid = false

      this.positionHandler.zoom(scale)
    } else {
      console.warn('ScaleHandler is not initialized')
    }
  }

  /**
   * Returns the scale as a percentage of zoom
   */
  get zoom(): number {
    if (this.scaleHandler) {
      const percentage = this.scaleHandler.getPercentageFromScale(this.scaleHandler.scale)
      return percentage
    }
    return 0
  }

  /**
   * Sets the zIndex of the main container and stores the value
   */
  set zIndex(zIndex: number) {
    this._zIndex = zIndex
    this.container.style.zIndex = `${this.zIndex}`
  }

  /**
   * Returns the zIndex value
   */
  get zIndex(): number {
    return this._zIndex
  }
}
